/**
 * 卖家端的令牌保管。
 *
 * <h2>🔴 为什么是 Bearer 而不是 cookie</h2>
 * `erp-web/README.md` §二② 当时写的是「Web 用 cookie、插件用 Bearer」，
 * 那是在后端登录契约落地**之前**写的猜测。
 * ⚠️ **实测后端发的是响应体里的 `accessToken`，没有任何 Set-Cookie** ——
 * ⇒ Web 端只能用 Bearer。这一段记在这里，免得有人照着 README 那句去找 cookie。
 *
 * <h2>🔴 存哪里：`localStorage`（2026-09-10 用户要求登录态保持）</h2>
 * access token 有效期只有 15 分钟，refresh token 才是长的那个。
 * 用户明确要求登录状态保持（浏览器重开不掉线）——插件 auth-sync 自愈推送
 * 依赖网页侧有令牌可用；sessionStorage 每次浏览器重开就空，导致插件反复失联。
 * 令牌有 refreshToken 过期时间兜底（90 天），登出即清除。
 *
 * 关闭后台标签不主动登出；续期始终由同源网页完成，插件只持有短期 EXTENSION access。
 * 在共用电脑上应主动退出；退出会清除网页会话并同步清除插件登录状态。
 *
 * <h2>⛔ 不做的事</h2>
 * ⛔ **不存明文口令**（哪怕是"记住密码"）；
 * ⛔ **不把令牌打进 console/日志** —— 一次录屏或一份浏览器日志就泄了。
 */

import { isAuthId, type AuthId } from '@erp/shared'

const KEY = 'erp.seller.auth'
const FORWARDER_KEY = 'erp.forwarder.auth'
export const UNAUTHORIZED_REDIRECT_KEY = 'erp.seller.auth.redirecting'

function clearForwarderStorage(): void {
  try {
    localStorage.removeItem(FORWARDER_KEY)
  } catch {
    // 存储被浏览器禁用时，不能让清理失败反过来阻断当前主体登录/退出。
  }
}

export interface SellerSession {
  userId: AuthId
  /**
   * 🔴 `TENANT` / `FORWARDER` / `OPERATOR` —— **本应用同时是三端的登录入口**。
   * 路由守卫按它决定能进哪些页面（见 router/index.ts）。
   * ⚠️ 它来自**令牌**，不是页面上选的那个角色 ——
   *    选错角色的结果是 `99405` 登不进来，⛔ 而不是拿到一个错的 subjectType。
   */
  subjectType: string
  /** 🔴 只有 TENANT 有值（后端契约：货代与运营都是 null） */
  tenantId: AuthId
  /** 🔴 只有 FORWARDER 有值（= forwarder_id）；TENANT 与 OPERATOR 都是 null */
  subjectId: AuthId
  /** true ⇒ 除改密/登出外一切接口返回 99406 */
  mustChangePassword: boolean
}

/** 主体类型常量 —— ⛔ 不要在各处散写字符串字面量 */
export const SUBJECT = {
  TENANT: 'TENANT',
  FORWARDER: 'FORWARDER',
  OPERATOR: 'OPERATOR',
} as const

interface Stored {
  accessToken: string
  /** 绝对时刻（ms）。⚠️ 由相对秒 `accessExpiresIn` + 收到那一刻算出 */
  accessExpiresAtMs: number
  refreshToken: string
  refreshExpiresAtMs: number
  session: SellerSession
}

type Listener = () => void
const listeners = new Set<Listener>()
let extensionSyncTimer: ReturnType<typeof setTimeout> | null = null

export function onAuthChanged(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Stored>
    // access 过期不等于整个会话过期：refresh token 仍然可以把它续上。
    // 如果在这里把 accessExpiresAtMs 也作为 read() 的准入条件，HTTP 层在真正
    // 需要刷新时反而再也读不到 refresh token，卖家就会每 15 分钟被踢一次。
    const valid = typeof parsed.accessToken === 'string' && parsed.accessToken.length > 0
      && typeof parsed.refreshToken === 'string' && parsed.refreshToken.length > 0
      && typeof parsed.accessExpiresAtMs === 'number'
      && typeof parsed.refreshExpiresAtMs === 'number' && parsed.refreshExpiresAtMs > Date.now()
      && parsed.session !== null && typeof parsed.session === 'object'
      && typeof parsed.session.subjectType === 'string'
      && isAuthId(parsed.session.userId)
      && isAuthId(parsed.session.tenantId)
      && isAuthId(parsed.session.subjectId)
    if (!valid) {
      localStorage.removeItem(KEY)
      return null
    }
    return parsed as Stored
  } catch {
    // ⛔ 不是空 catch：存储坏了就当没登录，但留一条能被搜到的日志
    console.warn('[seller] 会话读取失败，按未登录处理')
    return null
  }
}

export function saveTokens(pair: {
  accessToken: string
  accessExpiresIn: number
  refreshToken: string
  refreshExpiresIn: number
  session: SellerSession
}): void {
  const now = Date.now()
  // 清理旧版货代副本；内嵌作业台与管理后台统一读取本会话，不复制轮换令牌。
  clearForwarderStorage()
  sessionStorage.removeItem(UNAUTHORIZED_REDIRECT_KEY)
  const stored: Stored = {
    accessToken: pair.accessToken,
    accessExpiresAtMs: now + pair.accessExpiresIn * 1000,
    refreshToken: pair.refreshToken,
    refreshExpiresAtMs: now + pair.refreshExpiresIn * 1000,
    session: pair.session,
  }
  localStorage.setItem(KEY, JSON.stringify(stored))
  listeners.forEach((f) => f())

  // 🔴 登录态共享（2026-09-10 用户定稿）：登录成功后把 token 对推给浏览器插件。
  //    插件 ID 来自 CORS 配置（正式测试通道），externally_connectable 白名单保证只有我们的域名能发。
  //    发送失败静默——没装插件是常态，不能因为插件不在就报登录失败。
  void syncTokenToExtension(pair)
  scheduleExtensionSync()
}

// IDs are initialized before startup synchronization (the old eager IIFE hit the const TDZ).
export const EXTENSION_IDS = ['ofmkdljceinmdejjhnjilkcpbhknlegg', 'flbgomddlffakniiokekfphpbepfdoke']
let authSyncEpoch = 0
let syncInFlight: Promise<void> | null = null
export type ExtensionSyncState = 'IDLE' | 'UNAVAILABLE' | 'CONNECTING' | 'CONNECTED' | 'ERROR'
let extensionSyncState: ExtensionSyncState = 'IDLE'
let extensionSyncError = ''
export function currentExtensionSyncState(): ExtensionSyncState { return extensionSyncState }
export function currentExtensionSyncError(): string { return extensionSyncError }

function extensionMessage(id: string, message: unknown): Promise<boolean> {
  const runtime = typeof chrome !== 'undefined' ? chrome?.runtime : undefined
  const send = runtime?.sendMessage?.bind(runtime)
  if (!send) return Promise.resolve(false)
  return new Promise(resolve => {
    const timeout = setTimeout(() => resolve(false), 2500)
    try {
      send(id, message, (reply: unknown) => {
        clearTimeout(timeout)
        const error = runtime?.lastError
        const result = reply as { ok?: boolean; error?: { message?: unknown } } | undefined
        if ((message as { kind?: string })?.kind === 'auth-sync' && result?.ok === false && typeof result.error?.message === 'string') {
          extensionSyncError = result.error.message
        }
        resolve(!error && !!reply && typeof reply === 'object' && (reply as { ok?: boolean }).ok === true)
      })
    } catch { clearTimeout(timeout); resolve(false) }
  })
}

function clearExtensionSessions(): void {
  ++authSyncEpoch
  if (extensionSyncTimer) clearTimeout(extensionSyncTimer)
  extensionSyncTimer = null
  extensionSyncState = 'IDLE'
  extensionSyncError = ''
  for (const id of EXTENSION_IDS) void extensionMessage(id, { kind: 'auth-clear' })
}

function scheduleExtensionSync(grantExpiresAt?: number, retry = false): void {
  if (extensionSyncTimer) clearTimeout(extensionSyncTimer)
  extensionSyncTimer = null
  const stored = read()
  if (!stored || stored.session.subjectType !== SUBJECT.TENANT || stored.session.mustChangePassword) return
  const expiresAt = Math.min(stored.accessExpiresAtMs, grantExpiresAt ?? stored.accessExpiresAtMs)
  const lead = Math.min(60_000, Math.max(5_000, (expiresAt - Date.now()) / 2))
  const delay = retry ? 30_000 : Math.max(1_000, expiresAt - Date.now() - lead)
  extensionSyncTimer = setTimeout(() => {
    extensionSyncTimer = null
    void syncCurrentSessionToExtension().catch(() => scheduleExtensionSync(undefined, true))
  }, delay)
}

/** The webpage alone owns refresh. It sends only a server-derived, short EXTENSION access token. */
async function syncTokenToExtension(pair: {
  accessToken: string; accessExpiresIn: number; refreshToken: string; refreshExpiresIn: number; session: SellerSession
}): Promise<void> {
  const epoch = ++authSyncEpoch
  if (pair.session.subjectType !== SUBJECT.TENANT || !pair.session.tenantId || pair.session.mustChangePassword) {
    clearExtensionSessions()
    return
  }
  const installed = await Promise.all(EXTENSION_IDS.map(async id => await extensionMessage(id, { kind: 'ping' }) ? id : null))
  if (epoch !== authSyncEpoch) return
  const targets = installed.filter((id): id is string => id !== null)
  if (!targets.length) { extensionSyncState = 'UNAVAILABLE'; scheduleExtensionSync(undefined, true); return }
  extensionSyncState = 'CONNECTING'
  extensionSyncError = ''
  try {
    const response = await fetch('/api/v1/extension/session-sync', {
      method: 'POST', headers: { Authorization: `Bearer ${pair.accessToken}`, Accept: 'application/json' },
      credentials: 'omit', redirect: 'error', signal: AbortSignal.timeout(8000),
    })
    if (epoch !== authSyncEpoch || read()?.accessToken !== pair.accessToken) return
    if (response.status === 401 || response.status === 403) { clearExtensionSessions(); return }
    const envelope = await response.json()
    const grant = envelope.data
    if (!response.ok || envelope.code !== 0 || grant?.subjectType !== SUBJECT.TENANT
      || String(grant.tenantId) !== String(pair.session.tenantId) || String(grant.userId) !== String(pair.session.userId)
      || typeof grant.identityId !== 'string' || typeof grant.accessToken !== 'string'
      || !Number.isFinite(grant.accessExpiresIn) || grant.accessExpiresIn <= 5 || 'refreshToken' in grant) throw new Error('Invalid extension session')
    if (epoch !== authSyncEpoch || read()?.accessToken !== pair.accessToken) return
    const replies = await Promise.all(targets.map(id => extensionMessage(id, { kind: 'auth-sync', token: grant, apiBase: window.location.origin })))
    if (epoch === authSyncEpoch) {
      extensionSyncState = replies.some(Boolean) ? 'CONNECTED' : 'ERROR'
      if (extensionSyncState === 'CONNECTED') scheduleExtensionSync(Date.now() + grant.accessExpiresIn * 1000)
      else scheduleExtensionSync(undefined, true)
    }
  } catch {
    // A transient failure must not erase an already valid short token; expiry is still enforced by the extension.
    if (epoch === authSyncEpoch) { extensionSyncState = 'ERROR'; scheduleExtensionSync(undefined, true) }
  }
}

export function syncCurrentSessionToExtension(): Promise<void> {
  if (syncInFlight) return syncInFlight
  syncInFlight = (async () => {
    const stored = read()
    if (!stored) { clearExtensionSessions(); return }
    if (stored.accessExpiresAtMs - Date.now() <= 60000) {
      const { refreshAccessToken } = await import('./http')
      const refreshed = await refreshAccessToken()
      // Cross-tab rotation can return an already renewed WEB session without calling this tab's saveTokens.
      const renewed = refreshed ? read() : null
      if (renewed) await syncTokenToExtension({ ...renewed,
        accessExpiresIn: Math.floor((renewed.accessExpiresAtMs - Date.now()) / 1000),
        refreshExpiresIn: Math.floor((renewed.refreshExpiresAtMs - Date.now()) / 1000),
      })
      if (!refreshed && read()) scheduleExtensionSync(undefined, true)
      return
    }
    await syncTokenToExtension({ ...stored,
      accessExpiresIn: Math.floor((stored.accessExpiresAtMs - Date.now()) / 1000),
      refreshExpiresIn: Math.floor((stored.refreshExpiresAtMs - Date.now()) / 1000),
    })
  })().finally(() => { syncInFlight = null })
  return syncInFlight
}

export function clearTokens(): void {
  if (extensionSyncTimer) clearTimeout(extensionSyncTimer)
  extensionSyncTimer = null
  localStorage.removeItem(KEY)
  clearExtensionSessions()
  sessionStorage.removeItem(UNAUTHORIZED_REDIRECT_KEY)
  clearForwarderStorage()
  listeners.forEach((f) => f())
}

export function currentSession(): SellerSession | null {
  return read()?.session ?? null
}

export function hasSession(): boolean {
  return read() !== null
}

export function refreshTokenOrNull(): string | null {
  return read()?.refreshToken ?? null
}

/**
 * 请求头。
 * 🔴 没登录时返回**空对象**而不是一个假的 Bearer ——
 *    带着 `Bearer null` 去请求，后端回的是"令牌无效"，
 *    而真实原因是"根本没登录"，两者的处置不同。
 */
export function bearerHeader(): Record<string, string> {
  const s = read()
  return s ? { Authorization: `Bearer ${s.accessToken}` } : {}
}

/** access token 是否快过期了（默认提前 60 秒判定） */
export function accessTokenExpiringWithin(skewMs = 60_000): boolean {
  const s = read()
  return s !== null && s.accessExpiresAtMs - Date.now() < skewMs
}

// ---------------------------------------------------------------------------
// 货代端交接
// ---------------------------------------------------------------------------

/** Check the already-saved shared WEB session at the embedded workbench entry.
 * Do not create a second consumer of its single-use refresh token. */
export function handOffToForwarder(pair: {
  accessToken: string
  accessExpiresIn: number
  refreshToken: string
  refreshExpiresIn: number
  session: SellerSession
}): boolean {
  // The embedded workbench uses this same store and refresh owner. Never copy a
  // single-use refresh token into a second client (including the standalone PWA).
  const stored = read()
  clearForwarderStorage()
  return stored?.session.subjectType === SUBJECT.FORWARDER
    && stored.accessToken === pair.accessToken
    && stored.session.userId === pair.session.userId
    && stored.session.subjectId === pair.session.subjectId
}

// No eager IIFE before constants. Refreshing/reopening the backend page repairs a missed installation-time sync.
if (typeof window !== 'undefined') {
  queueMicrotask(() => { scheduleExtensionSync(); void syncCurrentSessionToExtension().catch(() => { extensionSyncState = 'ERROR' }) })
  window.addEventListener('focus', () => { void syncCurrentSessionToExtension().catch(() => { extensionSyncState = 'ERROR' }) })
  window.addEventListener('storage', event => {
    if (event.key === KEY || event.key === null) void syncCurrentSessionToExtension().catch(() => { extensionSyncState = 'ERROR' })
  })
}
